import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { useScheduleI18n } from '../../i18n'
import type { ScheduleEvent, ScheduleEventId } from '../../types'
import { dsText, fontSize, tokenVar } from '../../utils'
import {
  getCustomOverrideEvents,
  getHolidayOverrideEvents,
  getOverrideEvents,
  processScheduleEvents,
  ScheduleEventHelper,
} from '../../utils/scheduleEvent'
import { OverrideItem } from './OverrideItem'

export interface OverridesListProps {
  /** Schedule events from the API */
  events: ScheduleEvent[]
  /** Schedule timezone */
  scheduleTimeZone: string
  /** Whether editing is allowed */
  canEdit?: boolean
  /** Whether deletion is allowed */
  canDelete?: boolean
  /** Called when add override button is clicked */
  onAddOverride?: () => void
  /** Called when add holidays button is clicked */
  onAddHolidays?: () => void
  /** Called when edit button is clicked */
  onEditEvent?: (eventId: ScheduleEventId) => void
  /** Called when delete button is clicked */
  onDeleteEvent?: (eventId: ScheduleEventId, eventName: string) => void
  /** Custom edit icon for override items (defaults to built-in pencil icon) */
  editIcon?: ReactNode
  /** Custom delete icon for override items (defaults to built-in trash icon) */
  deleteIcon?: ReactNode
  /** Custom class name */
  className?: string
  /** Header title */
  title?: string
  /** Optional icon rendered before header title */
  titleIcon?: ReactNode
  /** Filter mode for displayed rows */
  mode?: 'all' | 'overrides' | 'holidays'
  /** Add button label override */
  addOverrideLabel?: string
  /** Add holidays button label override */
  addHolidaysLabel?: string
}

/**
 * List of schedule overrides (holidays, closures)
 * Displays non-FREE events sorted by start date
 */
export const OverridesList = ({
  events,
  scheduleTimeZone,
  canEdit = false,
  canDelete = false,
  onAddOverride,
  onAddHolidays,
  onEditEvent,
  onDeleteEvent,
  editIcon,
  deleteIcon,
  className = '',
  title,
  titleIcon,
  mode = 'all',
  addOverrideLabel,
  addHolidaysLabel,
}: OverridesListProps) => {
  const { text } = useScheduleI18n()
  const resolvedTitle =
    title ?? text('schedule.overrides.title', 'Schedule Overrides')
  const resolvedAddOverrideLabel =
    addOverrideLabel ??
    text('schedule.overrides.actions.addOverride', 'Add Override')
  const resolvedAddHolidaysLabel =
    addHolidaysLabel ??
    text('schedule.overrides.actions.addHoliday', 'Add Holiday')
  const emptyStateLabel = text(
    'schedule.overrides.empty',
    'No schedule overrides configured',
  )

  // Process events and filter to overrides only
  const overrideHelpers = useMemo(() => {
    const helpers = processScheduleEvents(events, scheduleTimeZone)
    if (mode === 'overrides') {
      return getCustomOverrideEvents(helpers)
    }
    if (mode === 'holidays') {
      return getHolidayOverrideEvents(helpers)
    }
    return getOverrideEvents(helpers)
  }, [events, scheduleTimeZone, mode])

  return (
    <div className={`overrides-list ${className}`}>
      <style>{`
        @media (max-width: 900px) {
          .overrides-list__header {
            flex-direction: column;
            align-items: stretch !important;
          }

          .overrides-list__header-actions {
            width: 100%;
          }

          .overrides-list__header-btn {
            flex: 1 1 0;
            justify-content: center;
          }
        }
      `}</style>
      <div
        className="overrides-list__header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '12px',
          gap: '12px',
        }}
      >
        <div
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
        >
          {titleIcon && (
            <span
              style={{
                display: 'inline-flex',
                color: tokenVar('fg.muted'),
              }}
            >
              {titleIcon}
            </span>
          )}
          <h4
            style={{
              margin: 0,
              fontFamily: dsText.Body1_Bold.fontFamily,
              fontSize: fontSize(dsText.Body1_Bold.fontSize),
              fontWeight: dsText.Body1_Bold.fontWeight,
              lineHeight: dsText.Body1_Bold.lineHeight,
              color: tokenVar('fg.default'),
            }}
          >
            {resolvedTitle}
          </h4>
        </div>

        {(canEdit || onAddOverride || onAddHolidays) && (
          <div
            className="overrides-list__header-actions"
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            {onAddOverride && (
              <button
                className="overrides-list__header-btn"
                type="button"
                onClick={onAddOverride}
                disabled={!canEdit}
                style={{
                  minHeight: '36px',
                  padding: '8px 14px',
                  cursor: canEdit ? 'pointer' : 'not-allowed',
                  backgroundColor: canEdit
                    ? tokenVar('blue.subtle')
                    : tokenVar('gray.subtle'),
                  color: canEdit ? tokenVar('blue.fg') : tokenVar('fg.muted'),
                  fontFamily: dsText.Body2.fontFamily,
                  fontSize: fontSize(dsText.Body2.fontSize),
                  fontWeight: dsText.Body2_Bold.fontWeight,
                  lineHeight: dsText.Body2.lineHeight,
                  border: `1px solid ${
                    canEdit
                      ? tokenVar('blue.muted')
                      : tokenVar('border.default')
                  }`,
                  borderRadius: '6px',
                  transition: 'background-color 0.2s',
                }}
              >
                + {resolvedAddOverrideLabel}
              </button>
            )}
            {onAddHolidays && (
              <button
                className="overrides-list__header-btn"
                type="button"
                onClick={onAddHolidays}
                disabled={!canEdit}
                style={{
                  minHeight: '36px',
                  padding: '8px 14px',
                  cursor: canEdit ? 'pointer' : 'not-allowed',
                  backgroundColor: canEdit
                    ? tokenVar('blue.subtle')
                    : tokenVar('gray.subtle'),
                  color: canEdit ? tokenVar('blue.fg') : tokenVar('fg.muted'),
                  fontFamily: dsText.Body2.fontFamily,
                  fontSize: fontSize(dsText.Body2.fontSize),
                  fontWeight: dsText.Body2_Bold.fontWeight,
                  lineHeight: dsText.Body2.lineHeight,
                  border: `1px solid ${
                    canEdit
                      ? tokenVar('blue.muted')
                      : tokenVar('border.default')
                  }`,
                  borderRadius: '6px',
                  transition: 'background-color 0.2s',
                }}
              >
                + {resolvedAddHolidaysLabel}
              </button>
            )}
          </div>
        )}
      </div>

      <div
        className="overrides-list__items"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          backgroundColor: tokenVar('bg.default'),
        }}
      >
        {overrideHelpers.length === 0 ? (
          <div
            style={{
              padding: '16px',
              borderRadius: '10px',
              border: `1px solid ${tokenVar('border.default')}`,
              backgroundColor: tokenVar('gray.subtle'),
              textAlign: 'left',
              color: tokenVar('fg.muted'),
              fontFamily: dsText.Body2.fontFamily,
              fontSize: fontSize(dsText.Body2.fontSize),
              lineHeight: dsText.Body2.lineHeight,
            }}
          >
            {emptyStateLabel}
          </div>
        ) : (
          overrideHelpers.map((helper: ScheduleEventHelper) => (
            <div key={helper.getId()}>
              <OverrideItem
                eventHelper={helper}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={onEditEvent}
                onDelete={onDeleteEvent}
                editIcon={editIcon}
                deleteIcon={deleteIcon}
              />
            </div>
          ))
        )}
      </div>
    </div>
  )
}
